import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

import {
  Plus,
  PawPrint,
  MoreVertical,
  Pencil,
  Trash2,
  Loader2,
  AlertTriangle,
  Dog,
  Cat,
  Bird,
  Rabbit,
  Fish
} from 'lucide-react';

const iconOptions = [
  { name: 'Dog', icon: Dog },
  { name: 'Cat', icon: Cat },
  { name: 'Bird', icon: Bird },
  { name: 'Rabbit', icon: Rabbit },
  { name: 'Fish', icon: Fish },
  { name: 'Other', icon: PawPrint }
];

export const PetTypes = () => {
  const [petTypes, setPetTypes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingPetTypeId, setDeletingPetTypeId] = useState(null);
  const [formData, setFormData] = useState({ name: '', icon: 'Other', description: '' });

  useEffect(() => {
    fetchPetTypes();
  }, []);

  const fetchPetTypes = async () => {
    try {
      const response = await api.get('/pet-types');
      setPetTypes(response.data);
    } catch (error) {
      toast.error('Failed to load pet types');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/pet-types', formData);
      toast.success('Pet type added successfully');
      setIsDialogOpen(false);
      setFormData({ name: '', icon: 'Other', description: '' });
      fetchPetTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const getIconComponent = (iconName) => {
    if (!iconName) return PawPrint; // Default for old data without icon
    const iconOption = iconOptions.find(opt => opt.name === iconName);
    return iconOption ? iconOption.icon : PawPrint;
  };

  const handleDelete = (id) => {
    setDeletingPetTypeId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/pet-types/${deletingPetTypeId}`);
      toast.success('Pet type deleted successfully');
      fetchPetTypes();
    } catch (error) {
      toast.error('Failed to delete pet type');
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingPetTypeId(null);
    }
  };

  return (
    <div className="p-6 md:p-12" data-testid="pet-types-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Pet Types</h1>
          <p className="text-gray-600">Manage pet type categories</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-lg"
              onClick={() => setFormData({ name: '', icon: 'Other', description: '' })}
              data-testid="add-pet-type-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Pet Type
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle>Add New Pet Type</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="pet-type-form">
              <div>
                <Label htmlFor="name">Pet Type Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  placeholder="e.g., Dog, Cat, Bird"
                  data-testid="pet-type-name-input"
                />
              </div>

              <div className="space-y-2">
              <Label>Icon</Label>
              <div className="grid grid-cols-6 gap-2">
                {iconOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.name}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: option.name })}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        formData.icon === option.name
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`h-5 w-5 mx-auto ${
                        formData.icon === option.name ? 'text-emerald-600' : 'text-slate-400'
                      }`} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
              <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="pet-type-submit-button">
                Add Pet Type
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {petTypes.length === 0 ? (
          <div className="col-span-4 text-center py-12 text-gray-500">
            No pet types found. Add your first pet type to get started!
          </div>
        ) : (
          petTypes.map((type, idx) => {
            const IconComponent = getIconComponent(type.icon);
            return (
            <div
              key={type.id}
              className="bg-white rounded-3xl shadow-lg p-6 text-center hover:shadow-2xl transition-all duration-300 hover:scale-105 relative group"
              data-testid={`pet-type-card-${idx}`}
            >
              <IconComponent className="w-12 h-12 mx-auto mb-3 text-sage-600" />
              <h3 className="text-lg font-heading font-bold text-sage-800">{type.name}</h3>
              {type.description && (
                <p className="text-sm text-gray-500 mt-2">{type.description}</p>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDelete(type.id)}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid={`delete-pet-type-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            );
          })
        )}
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the pet type.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-xl">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
